import { useState } from "react";
import { formatDate } from "@/lib/date";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  CalendarDays,
  Check,
  Eye,
  FileText,
  Layers,
  Loader2,
  Package,
  PackageMinus,
  PackagePlus,
  Search,
} from "lucide-react";
import { EmptyState } from "@/components/ui/empty-state";
import { toast } from "sonner";
import {
  useCartridges,
  useCartridgeCatalog,
  useCartridgesPaginated,
} from "@/hooks/useCartridges";
import { installCartridge, uninstallCartridge } from "@/api/cartridges";
import { ApiError } from "@/api/client";
import { parsePlanGate, type PlanGate } from "@/lib/errors";
import { UpsellInterstitial } from "@/components/UpsellInterstitial";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { usePageState } from "@/hooks/usePageState";
import { useResetPageOnChange } from "@/hooks/useResetPageOnChange";
import { useSearchQueryState } from "@/hooks/useSearchQueryState";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/ui/page-header";
import { VisibilityBadge } from "@/components/ui/visibility-badge";
import {
  AlertDialog, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { TableSkeleton } from "@/components/ui/table-skeleton";
import { TablePagination } from "@/components/ui/table-pagination";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export function CartridgesPage() {
  const { t } = useTranslation();
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("installed");

  // Installed tab — paginated + searchable (S10400.14 T4). Namespaced
  // ("installed_page"/"installed_pageSize") so it doesn't collide with the
  // Catalog tab's own query params below (S10400.15 AC6).
  const { page, pageSize, totalPages, setPage, handlePageSizeChange } =
    usePageState(10, { namespace: "installed" });
  const [search, setSearch] = useSearchQueryState({ namespace: "installed" });
  const debouncedSearch = useDebouncedValue(search, 300);
  useResetPageOnChange(debouncedSearch, setPage);
  const {
    cartridges,
    total: installedTotal,
    loading,
    error,
    refetch: refetchInstalled,
  } = useCartridgesPaginated(page, pageSize, debouncedSearch);

  // Unpaginated — feeds only the Catalog tab's installedNames cross-reference
  // below (D1 in the design doc): the full name set is cheap, unlike
  // rendering every row, so this is kept separate from the paginated table.
  const { cartridges: allInstalled, refetch: refetchInstalledNames } =
    useCartridges();

  // Catalog tab — paginated + searchable (S10400.14 T5). Independent
  // usePageState instance (D4): Installed and Catalog are separate
  // paginated resources, not a shared page cursor. Namespaced
  // ("catalog_page"/"catalog_pageSize") for the same reason as Installed.
  const {
    page: catalogPage,
    pageSize: catalogPageSize,
    totalPages: catalogTotalPages,
    setPage: setCatalogPage,
    handlePageSizeChange: handleCatalogPageSizeChange,
  } = usePageState(10, { namespace: "catalog" });
  const [keyword, setKeyword] = useSearchQueryState({ namespace: "catalog" });
  const debouncedKeyword = useDebouncedValue(keyword, 300);
  const [installing, setInstalling] = useState<Set<string>>(new Set());
  const [uninstallTarget, setUninstallTarget] = useState<string | null>(null);
  const [uninstalling, setUninstalling] = useState(false);
  const [upsellData, setUpsellData] = useState<PlanGate | null>(null);

  useResetPageOnChange(debouncedKeyword, setCatalogPage);

  const { catalog, loading: catalogLoading } = useCartridgeCatalog({
    keyword: debouncedKeyword,
    limit: catalogPageSize,
    offset: (catalogPage - 1) * catalogPageSize,
  });

  const installedNames = new Set(allInstalled.map((c) => c.cartridge_name));

  function refetchAllInstalled() {
    refetchInstalled();
    refetchInstalledNames();
  }

  async function handleInstall(name: string) {
    setInstalling((prev) => new Set(prev).add(name));
    try {
      await installCartridge(name);
      refetchAllInstalled();
      toast.success(t("cartridges.toast.installed", { name }));
    } catch (err) {
      const planGate = parsePlanGate(err);
      if (planGate) {
        setUpsellData(planGate);
      } else {
        toast.error(t("cartridges.toast.installFailed", { name }));
      }
    } finally {
      setInstalling((prev) => {
        const next = new Set(prev);
        next.delete(name);
        return next;
      });
    }
  }

  async function handleUninstall() {
    if (!uninstallTarget) return;
    const target = uninstallTarget;
    setUninstalling(true);
    try {
      await uninstallCartridge(target);
      refetchAllInstalled();
      toast.success(t("cartridges.toast.uninstalled", { name: target }));
      setUninstallTarget(null);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        toast.error(t("cartridges.toast.cannotUninstall", { name: target }));
        setUninstallTarget(null);
      } else {
        toast.error(t("cartridges.toast.uninstallFailed", { name: target }));
      }
    } finally {
      setUninstalling(false);
    }
  }

  function closeUninstallDialog() {
    setUninstallTarget(null);
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Package}
        title={t("cartridges.header.title")}
        subtitle={t("cartridges.header.subtitle")}
        secondaryGlow={false}
        stats={
          !loading && installedTotal > 0
            ? [{ value: installedTotal, label: t("cartridges.stats.total") }]
            : undefined
        }
      />
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="installed">{t("cartridges.tabs.installed")}</TabsTrigger>
          <TabsTrigger value="catalog">{t("cartridges.tabs.catalog")}</TabsTrigger>
        </TabsList>
        <TabsContent value="installed">
          <div className="space-y-4">
            <div className="relative max-w-sm">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                className="pl-8"
                placeholder={t("cartridges.search.placeholder")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            {error && (
              <p className="text-sm text-destructive">{error}</p>
            )}
            {!loading && installedTotal === 0 ? (
              <EmptyState
                icon={Package}
                title={t("cartridges.empty.title")}
                description={t("cartridges.empty.description")}
                action={{
                  label: t("cartridges.browseCatalog"),
                  onClick: () => setActiveTab("catalog"),
                }}
              />
            ) : (
              <div className="rounded-lg border bg-card">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead>
                        <span className="flex items-center gap-1.5"><Package className="h-3.5 w-3.5" />{t("cartridges.table.name")}</span>
                      </TableHead>
                      <TableHead>
                        <span className="flex items-center gap-1.5"><Layers className="h-3.5 w-3.5" />{t("cartridges.table.nodes")}</span>
                      </TableHead>
                      <TableHead>
                        <span className="flex items-center gap-1.5"><Eye className="h-3.5 w-3.5" />{t("cartridges.table.visibility")}</span>
                      </TableHead>
                      <TableHead>
                        <span className="flex items-center gap-1.5"><FileText className="h-3.5 w-3.5" />{t("cartridges.table.description")}</span>
                      </TableHead>
                      <TableHead>
                        <span className="flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5" />{t("cartridges.table.installed")}</span>
                      </TableHead>
                      <TableHead className="w-[100px]" />
                    </TableRow>
                  </TableHeader>
                  {loading ? (
                    <TableSkeleton columns={6} />
                  ) : (
                    <TableBody>
                      {cartridges.map((c) => (
                        <TableRow
                          key={c.cartridge_name}
                          className="cursor-pointer"
                          onClick={() => navigate(`/${orgSlug}/cartridges/${c.cartridge_name}`)}
                        >
                          <TableCell className="font-medium">{c.cartridge_name}</TableCell>
                          <TableCell>{c.node_count}</TableCell>
                          <TableCell>
                            <VisibilityBadge visibility={c.visibility} />
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {c.description ?? "—"}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {formatDate(c.installed_at)}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:text-destructive"
                              onClick={(e) => {
                                e.stopPropagation();
                                setUninstallTarget(c.cartridge_name);
                              }}
                            >
                              <PackageMinus className="h-4 w-4" />
                              {t("cartridges.uninstallButton")}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  )}
                </Table>
                <TablePagination
                  currentPage={page}
                  totalPages={totalPages(installedTotal)}
                  onPrev={() => setPage((p) => p - 1)}
                  onNext={() => setPage((p) => p + 1)}
                  hasNext={page < totalPages(installedTotal)}
                  hasPrev={page > 1}
                  pageSize={pageSize}
                  onPageSizeChange={handlePageSizeChange}
                />
              </div>
            )}
          </div>
        </TabsContent>
        <TabsContent value="catalog">
          <div className="space-y-4">
            <div className="relative max-w-sm">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                className="pl-8"
                placeholder={t("cartridges.catalog.searchPlaceholder")}
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
              />
            </div>
            {!catalogLoading && catalog.total_count === 0 ? (
              <EmptyState
                icon={Search}
                title={debouncedKeyword ? t("cartridges.catalog.empty.titleNoResults") : t("cartridges.catalog.empty.titleEmpty")}
                description={
                  debouncedKeyword
                    ? t("cartridges.catalog.empty.descriptionNoResults", { keyword: debouncedKeyword })
                    : t("cartridges.catalog.empty.descriptionEmpty")
                }
              />
            ) : (
              <div className="rounded-lg border bg-card">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead>
                        <span className="flex items-center gap-1.5"><Package className="h-3.5 w-3.5" />{t("cartridges.table.name")}</span>
                      </TableHead>
                      <TableHead>
                        <span className="flex items-center gap-1.5"><Layers className="h-3.5 w-3.5" />{t("cartridges.table.nodes")}</span>
                      </TableHead>
                      <TableHead>
                        <span className="flex items-center gap-1.5"><Eye className="h-3.5 w-3.5" />{t("cartridges.table.visibility")}</span>
                      </TableHead>
                      <TableHead>
                        <span className="flex items-center gap-1.5"><FileText className="h-3.5 w-3.5" />{t("cartridges.table.description")}</span>
                      </TableHead>
                      <TableHead className="w-[100px]" />
                    </TableRow>
                  </TableHeader>
                  {catalogLoading ? (
                    <TableSkeleton columns={5} />
                  ) : (
                    <TableBody>
                      {catalog.items.map((c) => (
                        <TableRow
                          key={c.cartridge_name}
                          className="cursor-pointer"
                          onClick={() => navigate(`/${orgSlug}/cartridges/${c.cartridge_name}`)}
                        >
                          <TableCell className="font-medium">{c.cartridge_name}</TableCell>
                          <TableCell>{c.node_count}</TableCell>
                          <TableCell>
                            <VisibilityBadge visibility={c.visibility ?? "public"} />
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {c.description ?? "—"}
                          </TableCell>
                          <TableCell>
                            {installedNames.has(c.cartridge_name) ? (
                              <Button variant="ghost" size="sm" disabled className="text-muted-foreground">
                                <Check className="h-4 w-4" />
                                {t("cartridges.catalog.installed")}
                              </Button>
                            ) : installing.has(c.cartridge_name) ? (
                              <Button variant="ghost" size="sm" disabled>
                                <Loader2 className="h-4 w-4 animate-spin" />
                              </Button>
                            ) : (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleInstall(c.cartridge_name);
                                }}
                              >
                                <PackagePlus className="h-4 w-4" />
                                {t("cartridges.catalog.install")}
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  )}
                </Table>
                <TablePagination
                  currentPage={catalogPage}
                  totalPages={catalogTotalPages(catalog.total_count)}
                  onPrev={() => setCatalogPage((p) => p - 1)}
                  onNext={() => setCatalogPage((p) => p + 1)}
                  hasNext={catalogPage < catalogTotalPages(catalog.total_count)}
                  hasPrev={catalogPage > 1}
                  pageSize={catalogPageSize}
                  onPageSizeChange={handleCatalogPageSizeChange}
                />
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {upsellData && (
        <UpsellInterstitial
          requiredPlan={upsellData.required_plan}
          currentPlan={upsellData.current_plan}
          licenseHref={`/${orgSlug}/license`}
          onClose={() => setUpsellData(null)}
        />
      )}

      <AlertDialog open={uninstallTarget !== null} onOpenChange={(open) => { if (!open) closeUninstallDialog(); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("cartridges.uninstallDialog.title", { name: uninstallTarget })}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("cartridges.uninstallDialog.description")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="outline" onClick={closeUninstallDialog} disabled={uninstalling}>
              {t("cartridges.uninstallDialog.cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={handleUninstall}
              disabled={uninstalling}
            >
              {uninstalling ? <Loader2 className="h-4 w-4 animate-spin" /> : t("cartridges.uninstallDialog.confirm")}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
